import type { AgentComponent, AgentComponentType } from '@reach/shared-types/agent-components';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { Button } from '../../../components/ui/button';
import { useUnsavedChangesGuard } from '../../ai-tools/useUnsavedChangesGuard';
import { componentListPath } from '../agent-component-paths';
import { COMPONENT_TYPE_COPY } from '../agents-copy';
import { AgentInstructionsCard } from './AgentInstructionsCard';
import {
  bodyFrom,
  draftFrom,
  draftsMatch,
  type ComponentDraft,
  type ComponentDraftChange,
} from './component-draft';
import { COMPONENT_PAGE_COPY } from './component-page-copy';
import styles from './ComponentEditor.module.css';
import { DetailsCard } from './DetailsCard';
import { shortComponentName } from './short-component-name';
import { ModeField } from './ModeField';
import { TenantDisplayCard } from './TenantDisplayCard';
import { TenantPreviewCard } from './TenantPreviewCard';
import { useVoiceTemplateUsage } from '../useTemplateComponents';
import { useDeleteComponent, useSaveComponent } from './useComponentMutations';
import { UsedInCard } from './UsedInCard';

interface ComponentEditorProps {
  type: AgentComponentType;
  component?: AgentComponent;
}

/** Creates a component, or edits and deletes a saved one. */
export const ComponentEditor = ({ type, component }: ComponentEditorProps) => {
  const navigate = useNavigate();
  const savedDraft = draftFrom(component);
  const [draft, setDraft] = useState<ComponentDraft>(savedDraft);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const draftHasChanges = !draftsMatch(draft, savedDraft);
  const allowNextNavigation = useUnsavedChangesGuard(draftHasChanges);
  const saveComponent = useSaveComponent(type, component?.id, allowNextNavigation);
  const listPath = componentListPath(type);
  const deleteComponent = useDeleteComponent(() => {
    allowNextNavigation(listPath);
    navigate(listPath);
  });
  const { voiceTemplateUsesComponent, usageIsLoading, usageCheckFailed } = useVoiceTemplateUsage(
    component?.id,
  );

  const typeCopy = COMPONENT_TYPE_COPY[type];
  const componentIsNew = component === undefined;
  const title = component?.name ?? typeCopy.newLabel;
  const saveIsAvailable = draftHasChanges && !saveComponent.isPending;
  const discardIsAvailable = componentIsNew || draftHasChanges;
  const idleSaveLabel = componentIsNew ? typeCopy.createLabel : COMPONENT_PAGE_COPY.save;
  const saveLabel = saveComponent.isPending ? COMPONENT_PAGE_COPY.saving : idleSaveLabel;
  const componentShortName = shortComponentName(component?.name ?? '');
  const deleteTitle = COMPONENT_PAGE_COPY.deleteTitle(componentShortName);
  const deleteIsBlocked = voiceTemplateUsesComponent || usageIsLoading || usageCheckFailed;
  const knownUsageDeleteBody = voiceTemplateUsesComponent
    ? COMPONENT_PAGE_COPY.deleteBlockedBody(componentShortName)
    : typeCopy.deleteBody(componentShortName);
  const deleteBody = usageCheckFailed
    ? COMPONENT_PAGE_COPY.deleteUsageCheckFailedBody(componentShortName)
    : knownUsageDeleteBody;

  const updateDraft = (change: ComponentDraftChange) => setDraft(current => ({ ...current, ...change }));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    saveComponent.mutate(bodyFrom(type, draft));
  };

  const handleDiscard = () => {
    if (componentIsNew) {
      allowNextNavigation(listPath);
      navigate(listPath);
      return;
    }
    setDraft(savedDraft);
  };

  const confirmDelete = () => {
    setConfirmingDelete(false);
    if (component) {
      deleteComponent.mutate(component.id);
    }
  };

  return (
    <form className={styles.editor} onSubmit={handleSubmit}>
      <header className={styles.header}>
        <nav aria-label={COMPONENT_PAGE_COPY.breadcrumbLabel}>
          <ol>
            <li>
              <Link to={listPath}>{typeCopy.title}</Link>
            </li>
            <li aria-current="page">{title}</li>
          </ol>
        </nav>
        <h1>{title}</h1>
        <p>{typeCopy.typeLabel}</p>
        <div className={styles.actions}>
          {!componentIsNew && (
            <Button
              type="button"
              variant="ghost"
              disabled={deleteComponent.isPending}
              onClick={() => setConfirmingDelete(true)}>
              {COMPONENT_PAGE_COPY.delete}
            </Button>
          )}
          <Button type="button" variant="outline" disabled={!discardIsAvailable} onClick={handleDiscard}>
            {COMPONENT_PAGE_COPY.discard}
          </Button>
          <Button type="submit" disabled={!saveIsAvailable}>
            {saveLabel}
          </Button>
        </div>
      </header>

      <div className={styles.main}>
        <DetailsCard type={type} draft={draft} onDraftChange={updateDraft} />
        <AgentInstructionsCard type={type} draft={draft} onDraftChange={updateDraft} />
        <TenantDisplayCard type={type} draft={draft} onDraftChange={updateDraft} />
      </div>

      <aside className={styles.side}>
        <UsedInCard componentId={component?.id} />
        <ModeField type={type} value={draft.mode} onChange={mode => updateDraft({ mode })} />
        <TenantPreviewCard draft={draft} />
      </aside>

      {confirmingDelete && (
        <ConfirmDialog
          title={deleteTitle}
          confirmLabel={COMPONENT_PAGE_COPY.delete}
          onClose={() => setConfirmingDelete(false)}
          onConfirm={confirmDelete}
          disabled={deleteIsBlocked}>
          <p>{deleteBody}</p>
        </ConfirmDialog>
      )}
    </form>
  );
};
